import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  MenuItem,
  Box,
  Stepper,
  Step,
  StepLabel,
  Alert,
  CircularProgress
} from '@mui/material';
import { styled, createTheme } from '@mui/material/styles';
import imageCompression from 'browser-image-compression';

const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});

// Export the theme if needed elsewhere
export const theme = createTheme({
  palette: {
    primary: {
      main: '#5FC0C1',
    },
    secondary: {
      main: '#22c55e',
    },
  },
});

const initialForm = {
  year: '',
  id_number: '',
  gender: '',
  ethnicity: '',
  home_language: '',
  full_names: '',
  student_number: '',
  institution: '',
  email: '',
  phone: '',
  home_address: '',
  guardian_name: '',
  guardian_relationship: '',
  guardian_phone: '',
  guardian_email: ''
};

const ApplicationForm = () => {
  const navigate = useNavigate();
  const [form, setForm] = useState(initialForm);
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [applicationId, setApplicationId] = useState('');
  const [files, setFiles] = useState({
    photo: null,
    id_card_1: null,
    id_card_2: null,
    proof_of_registration: null,
  });

  // Validation functions
  const validateIDNumber = (id) => {
    const idRegex = /^\d{13}$/;
    return idRegex.test(id);
  };

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validatePhone = (phone) => {
    const phoneRegex = /^0\d{9}$/;
    return phoneRegex.test(phone.replace(/\s/g, ''));
  };

  const validateStudentNumber = (studentNum) => {
    const studentRegex = /^[A-Za-z0-9]{3,20}$/;
    return studentRegex.test(studentNum);
  };

  const validateNames = (name) => {
    const nameRegex = /^[A-Za-z\s]{2,}$/;
    return nameRegex.test(name.trim());
  };

  const handleFileChange = async (e) => {
    const { name, files: selectedFiles } = e.target;
    if (selectedFiles.length > 0) {
      const file = selectedFiles[0];
      console.log(`Selected ${name} file:`, file.name, `Size: ${(file.size / 1024 / 1024).toFixed(2)}MB`);
      
      // Compress image files to reduce upload size (Vercel has 4.5MB limit)
      if (file.type.startsWith('image/')) {
        try {
          const options = {
            maxSizeMB: 1, // Maximum 1MB per image
            maxWidthOrHeight: 1920, // Maximum dimension
            useWebWorker: true,
            fileType: 'image/jpeg' // Convert to JPEG for better compression
          };
          
          console.log(`Compressing ${name}...`);
          const compressedFile = await imageCompression(file, options);
          console.log(`Compressed ${name} from ${(file.size / 1024 / 1024).toFixed(2)}MB to ${(compressedFile.size / 1024 / 1024).toFixed(2)}MB`);
          
          setFiles((prev) => ({ ...prev, [name]: compressedFile }));
        } catch (error) {
          console.error(`Error compressing ${name}:`, error);
          // If compression fails, use original file
          setFiles((prev) => ({ ...prev, [name]: file }));
        }
      } else {
        // For PDFs, just use the file as-is
        setFiles((prev) => ({ ...prev, [name]: file }));
      }
      
      setFieldErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFieldErrors((prev) => ({ ...prev, [name]: '' }));

    let processedValue = value;
    let fieldErr = '';

    switch (name) {
      case 'id_number':
        processedValue = value.replace(/\D/g, '').slice(0, 13);
        if (processedValue && !validateIDNumber(processedValue)) {
          if (processedValue.length < 13) {
            fieldErr = 'ID number must be 13 digits';
          }
        }
        break;

      case 'phone':
      case 'guardian_phone':
        processedValue = value.replace(/\D/g, '').slice(0, 10);
        if (processedValue && !validatePhone(processedValue)) {
          if (processedValue.length < 10) {
            fieldErr = 'Phone number must be 10 digits starting with 0';
          } else if (!processedValue.startsWith('0')) {
            fieldErr = 'Phone number must start with 0';
          }
        }
        break;

      case 'email':
      case 'guardian_email':
        if (processedValue && !validateEmail(processedValue)) {
          fieldErr = 'Please enter a valid email address';
        }
        break;

      case 'student_number':
        processedValue = value.replace(/[^A-Za-z0-9]/g, '').slice(0, 20);
        if (processedValue && !validateStudentNumber(processedValue)) {
          if (processedValue.length < 3) {
            fieldErr = 'Student number must be at least 3 characters';
          }
        }
        break;

      case 'full_names':
      case 'guardian_name':
        processedValue = value.replace(/[^A-Za-z\s]/g, '');
        if (processedValue && !validateNames(processedValue)) {
          fieldErr = 'Name must contain only letters and spaces';
        }
        break;

      default:
        break;
    }

    setForm({ ...form, [name]: processedValue });

    if (fieldErr) {
      setFieldErrors((prev) => ({ ...prev, [name]: fieldErr }));
    }
  };

  const validateStep = () => {
    const errors = {};

    if (step === 1) {
      const requiredFields = [
        'year', 'full_names', 'id_number', 'student_number', 'institution',
        'email', 'phone', 'home_address', 'gender', 'ethnicity', 'home_language'
      ];

      requiredFields.forEach((field) => {
        if (!form[field] || form[field].trim() === '') {
          errors[field] = 'This field is required';
        }
      });

      if (form.id_number && !validateIDNumber(form.id_number)) {
        errors.id_number = 'ID number must be exactly 13 digits';
      }

      if (form.email && !validateEmail(form.email)) {
        errors.email = 'Please enter a valid email address';
      }

      if (form.phone && !validatePhone(form.phone)) {
        errors.phone = 'Phone number must be 10 digits starting with 0';
      }

      if (form.student_number && !validateStudentNumber(form.student_number)) {
        errors.student_number = 'Student number must be 3-20 alphanumeric characters';
      }

      if (form.full_names && !validateNames(form.full_names)) {
        errors.full_names = 'Name must contain only letters and spaces';
      }
    }

    if (step === 2) {
      if (!files.photo) {
        errors.photo = 'Student photo is required.';
      }
      if (!files.id_card_1) {
        errors.id_card_1 = 'Front of ID card is required.';
      }
      if (!files.id_card_2) {
        errors.id_card_2 = 'Back of ID card is required.';
      }
      if (!files.proof_of_registration) {
        errors.proof_of_registration = 'Proof of registration or acceptance letter is required.';
      }
    }

    if (step === 3) {
      const requiredFields = ['guardian_name', 'guardian_relationship', 'guardian_phone', 'guardian_email'];

      requiredFields.forEach((field) => {
        if (!form[field] || form[field].trim() === '') {
          errors[field] = 'This field is required';
        }
      });

      if (form.guardian_email && !validateEmail(form.guardian_email)) {
        errors.guardian_email = 'Please enter a valid email address';
      }

      if (form.guardian_phone && !validatePhone(form.guardian_phone)) {
        errors.guardian_phone = 'Phone number must be 10 digits starting with 0';
      }

      if (form.guardian_name && !validateNames(form.guardian_name)) {
        errors.guardian_name = 'Name must contain only letters and spaces';
      }
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleNext = () => {
    setError('');

    if (!validateStep()) {
      setError('Please fix the errors above before continuing');
      return;
    }

    if (step < 4) {
      setStep(step + 1);
    }
  };

  const handleBack = () => {
    setError('');
    setFieldErrors({});
    if (step > 1) {
      setStep(step - 1);
    }
  };

  const handleNewApplication = () => {
    setSubmitted(false);
    setApplicationId('');
    setError('');
    setForm(initialForm);
    setFieldErrors({});
    setFiles({ photo: null, id_card_1: null, id_card_2: null, proof_of_registration: null });
    setStep(1);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateStep()) {
      setError('Please fix all errors before submitting');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Create a new FormData instance
      const formData = new FormData();
      
      console.log('=== Starting Form Submission ===');
      console.log('Form state:', form);
      console.log('Files state:', files);
      
      // Debug helper to inspect FormData contents
      const logFormData = (formData) => {
        console.log('FormData contents:');
        for (let pair of formData.entries()) {
          const value = pair[1] instanceof File 
            ? `File: ${pair[1].name} (${pair[1].type})`
            : pair[1];
          console.log(`- ${pair[0]}: ${value}`);
        }
      };
      
      // First, validate required files
      const requiredFiles = {
        photo: { type: 'image', maxSize: 10 * 1024 * 1024 },
        id_card_1: { type: 'both', maxSize: 10 * 1024 * 1024 },
        id_card_2: { type: 'both', maxSize: 10 * 1024 * 1024 },
        proof_of_registration: { type: 'pdf', maxSize: 10 * 1024 * 1024 }
      };

      // Debug: Log all available files
      console.log('Available files in state:', Object.keys(files));
      console.log('Required files:', Object.keys(requiredFiles));

      // Validate and append files
      for (const [field, requirements] of Object.entries(requiredFiles)) {
        console.log(`Validating ${field}:`, { requirements });
        const file = files[field];
        console.log(`File for ${field}:`, file ? {
          name: file.name,
          type: file.type,
          size: file.size
        } : 'not found');
        
        if (!file) {
          throw new Error(`${field.replace(/_/g, ' ')} is required`);
        }

        // Validate file type - more lenient for mobile devices
        // Some mobile devices don't set proper MIME types, so also check file extension
        const fileName = file.name.toLowerCase();
        const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.heic', '.heif'];
        const pdfExtensions = ['.pdf'];
        const hasImageExt = imageExtensions.some(ext => fileName.endsWith(ext));
        const hasPdfExt = pdfExtensions.some(ext => fileName.endsWith(ext));
        
        if (requirements.type === 'image') {
          if (!file.type.startsWith('image/') && !hasImageExt) {
            throw new Error(`${field.replace(/_/g, ' ')} must be an image file (JPG, PNG, etc.)`);
          }
        }
        if (requirements.type === 'pdf') {
          if (file.type !== 'application/pdf' && !hasPdfExt) {
            throw new Error(`${field.replace(/_/g, ' ')} must be a PDF file`);
          }
        }
        if (requirements.type === 'both') {
          const isValidImage = file.type.startsWith('image/') || hasImageExt;
          const isValidPdf = file.type === 'application/pdf' || hasPdfExt;
          if (!isValidImage && !isValidPdf) {
            throw new Error(`${field.replace(/_/g, ' ')} must be an image or PDF file`);
          }
        }

        // Validate file size
        if (file.size > requirements.maxSize) {
          const sizeMB = (file.size / (1024 * 1024)).toFixed(2);
          throw new Error(`${field.replace(/_/g, ' ')} is too large (${sizeMB}MB). Maximum size is 10MB. Please compress the file or take a smaller photo.`);
        }

        // Append file with proper field name
        console.log(`Appending ${field}:`, file.name, file.type, file.size);
        formData.append(field, file, file.name); // Added filename to help with debugging
      }

      // Add form fields
      Object.entries(form).forEach(([key, value]) => {
        if (value !== null && value !== undefined) {
          console.log(`Appending form field ${key}:`, value);
          formData.append(key, String(value)); // Convert to string to ensure safe transmission
        }
      });

      // Log final FormData contents before submission
      console.log('=== Final FormData Contents ===');
      logFormData(formData);

      // Use relative path to call API from the same domain
      const url = `${window.location.origin}/api/apply`;
      
      console.log('Submitting to:', url);
      console.log('Request origin:', window.location.origin);
      console.log('=== Request Details ===');
      
      // Add timeout for slow mobile connections (60 seconds)
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 60000);
      
      let response;
      try {
        response = await fetch(url, {
          method: 'POST',
          body: formData,
          signal: controller.signal,
          mode: 'cors', // Explicitly set CORS mode
          credentials: 'same-origin', // Include credentials for same origin
          // Important: Don't set any Content-Type header, let the browser handle it
          headers: {
            'Accept': 'application/json',
            // Add cache control to bypass Cloudflare caching issues
            'Cache-Control': 'no-cache'
          }
        });
        clearTimeout(timeoutId);
      } catch (fetchError) {
        clearTimeout(timeoutId);
        if (fetchError.name === 'AbortError') {
          throw new Error('Upload timeout. Your connection may be slow. Please try again with smaller files or better internet connection.');
        }
        throw fetchError;
      }

      // First, get the raw response text for debugging
      const responseText = await response.text();
      console.log('=== Server Response ===');
      console.log('Status:', response.status);
      console.log('Status Text:', response.statusText);
      console.log('Headers:', Object.fromEntries([...response.headers]));
      console.log('Body:', responseText);

      // Try to parse it as JSON
      let result;
      try {
        result = JSON.parse(responseText);
        console.log('Parsed JSON Response:', result);
      } catch (parseError) {
        console.error('=== JSON Parse Error ===');
        console.error('Parse Error:', parseError);
        console.error('Raw Response:', responseText);
        console.error('Response Status:', response.status);
        
        // Special handling for 403 errors (Vercel security or file size limits)
        if (response.status === 403) {
          const isVercelBlock = responseText.includes('Forbidden') || responseText.includes('cpt1::');
          if (isVercelBlock) {
            throw new Error(
              'Your submission was blocked, likely due to file size limits (4.5MB maximum total). ' +
              'We automatically compress images, but please ensure your PDF proof of registration is under 3MB. ' +
              'Try using a smaller PDF file or compress it first. ' +
              'If the problem persists, please contact support.'
            );
          }
        }
        
        throw new Error(`Server returned invalid JSON. Status: ${response.status}, Body: ${responseText}`);
      }

      if (!response.ok) {
        // Log the full error details
        console.error('Server error details:', {
          status: response.status,
          statusText: response.statusText,
          body: result
        });
        
        throw new Error(
          result.error || result.message || 
          `Server error (${response.status}): ${JSON.stringify(result)}`
        );
      }

      // Check if the response indicates success
      if (result.success === true || result.id || result.message) {
        console.log('=== Submission Successful ===');
        console.log('Result:', result);
        setLoading(false);
        setSubmitted(true);
        setApplicationId(result.id || result.application_id || 'KHY' + Date.now());
      } else {
        throw new Error(result.error || 'Submission failed with unknown error');
      }
    } catch (err) {
      console.error('=== Submission Error ===');
      console.error('Error:', err);
      console.error('Error name:', err.name);
      console.error('Error message:', err.message);
      console.error('Error stack:', err.stack);
      
      setLoading(false);
      
      if (err.message === 'Failed to fetch') {
        setError('Unable to connect to server. Please check your internet connection and try again.');
        alert('Connection error. Please check your internet connection.');
      } else if (err.message.includes('Unexpected field')) {
        console.error('Files state when error occurred:', files);
        console.error('Form state when error occurred:', form);
        setError('File upload error: Unexpected field name. Please try again or contact support.');
        alert('File upload error. Please check your files and try again.');
      } else if (err.message.includes('timeout') || err.message.includes('Timeout')) {
        setError(err.message);
        alert('Upload timeout. Please try again with a better connection or smaller files.');
      } else {
        const errorMessage = err.message || 'An unexpected error occurred';
        console.error('Detailed error:', {
          message: errorMessage,
          error: err
        });
        setError(`Error: ${errorMessage}\n\nPlease check the browser console for more details.`);
        alert(`Submit failed: ${errorMessage}\n\nCheck console for details.`);
      }
    }
  };

  const steps = ['Personal Details', 'Supporting Documents', 'Guardian Information', 'Review & Submit'];

  const renderStepContent = () => {
    switch (step) {
      case 1:
        return (
          <Box>
            <Box sx={{ mb: 3, textAlign: 'center' }}>
              <Typography
                variant="h4"
                fontWeight={700}
                sx={{
                  color: '#5FC0C1',
                  mb: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 1
                }}
              >
                Riverview Application
              </Typography>
              <Typography
                variant="h6"
                fontWeight={600}
                sx={{
                  color: '#5FC0C1',
                  mb: 1
                }}
              >
                Personal Details
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Please provide your personal information
              </Typography>
            </Box>

            <TextField
              select
              label="Year of Application"
              name="year"
              value={form.year}
              onChange={handleChange}
              required
              fullWidth
              margin="normal"
              error={!!fieldErrors.year}
              helperText={fieldErrors.year}
            >
              <MenuItem value="">Select Year</MenuItem>
              <MenuItem value="2025">2025</MenuItem>
              <MenuItem value="2026">2026</MenuItem>
              <MenuItem value="2027">2027</MenuItem>
            </TextField>

            <TextField
              label="Full Names"
              name="full_names"
              value={form.full_names}
              onChange={handleChange}
              required
              fullWidth
              margin="normal"
              error={!!fieldErrors.full_names}
              helperText={fieldErrors.full_names || 'Enter your full name (letters and spaces only)'}
              placeholder="e.g. John Smith"
            />

            <TextField
              label="ID Number"
              name="id_number"
              value={form.id_number}
              onChange={handleChange}
              required
              fullWidth
              margin="normal"
              error={!!fieldErrors.id_number}
              helperText={fieldErrors.id_number || 'Enter your 13-digit South African ID number'}
              placeholder="e.g. 9001015009087"
              inputProps={{ maxLength: 13 }}
            />

            <TextField
              label="Student Number"
              name="student_number"
              value={form.student_number}
              onChange={handleChange}
              required
              fullWidth
              margin="normal"
              error={!!fieldErrors.student_number}
              helperText={fieldErrors.student_number || 'Enter your student number'}
              placeholder="e.g. STU12345"
            />

            <TextField
              select
              label="Institution"
              name="institution"
              value={form.institution}
              onChange={handleChange}
              required
              fullWidth
              margin="normal"
              error={!!fieldErrors.institution}
              helperText={fieldErrors.institution}
            >
              <MenuItem value="">Select Institution</MenuItem>
              <MenuItem value="UMP">UMP</MenuItem>
              <MenuItem value="Ehlandzeni TVET Collage">Ehlandzeni TVET Collage</MenuItem>
              <MenuItem value="University of Mpumalanga">University of Mpumalanga</MenuItem>
              <MenuItem value="Tshwane University of Technology">Tshwane University of Technology</MenuItem>
              <MenuItem value="Other">Other</MenuItem>
            </TextField>

            <TextField
              label="Email"
              type="email"
              name="email"
              value={form.email}
              onChange={handleChange}
              required
              fullWidth
              margin="normal"
              error={!!fieldErrors.email}
              helperText={fieldErrors.email || 'Enter a valid email address'}
              placeholder="e.g. john@example.com"
            />

            <TextField
              label="Phone Number"
              name="phone"
              value={form.phone}
              onChange={handleChange}
              required
              fullWidth
              margin="normal"
              error={!!fieldErrors.phone}
              helperText={fieldErrors.phone || 'Enter 10-digit phone number starting with 0'}
              placeholder="e.g. 0123456789"
              inputProps={{ maxLength: 10 }}
            />

            <TextField
              label="Home Address"
              name="home_address"
              value={form.home_address}
              onChange={handleChange}
              required
              fullWidth
              margin="normal"
              multiline
              rows={2}
              error={!!fieldErrors.home_address}
              helperText={fieldErrors.home_address}
              placeholder="Enter your full home address"
            />

            <TextField
              select
              label="Gender"
              name="gender"
              value={form.gender}
              onChange={handleChange}
              required
              fullWidth
              margin="normal"
              error={!!fieldErrors.gender}
              helperText={fieldErrors.gender}
            >
              <MenuItem value="">Select Gender</MenuItem>
              <MenuItem value="male">Male</MenuItem>
              <MenuItem value="female">Female</MenuItem>
              <MenuItem value="other">Other</MenuItem>
            </TextField>

            <TextField
              select
              label="Ethnicity"
              name="ethnicity"
              value={form.ethnicity}
              onChange={handleChange}
              required
              fullWidth
              margin="normal"
              error={!!fieldErrors.ethnicity}
              helperText={fieldErrors.ethnicity}
            >
              <MenuItem value="">Select Ethnicity</MenuItem>
              <MenuItem value="African">African</MenuItem>
              <MenuItem value="Coloured">Coloured</MenuItem>
              <MenuItem value="Indian">Indian</MenuItem>
              <MenuItem value="White">White</MenuItem>
              <MenuItem value="Other">Other</MenuItem>
            </TextField>

            <TextField
              select
              label="Home Language"
              name="home_language"
              value={form.home_language}
              onChange={handleChange}
              required
              fullWidth
              margin="normal"
              error={!!fieldErrors.home_language}
              helperText={fieldErrors.home_language}
            >
              <MenuItem value="">Select Language</MenuItem>
              <MenuItem value="siSwati">siSwati</MenuItem>
              <MenuItem value="isiZulu">isiZulu</MenuItem>
              <MenuItem value="isiXhosa">isiXhosa</MenuItem>
              <MenuItem value="Xitsonga">Xitsonga</MenuItem>
              <MenuItem value="English">English</MenuItem>
              <MenuItem value="Other">Other</MenuItem>
            </TextField>

            {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
              <Button
                variant="contained"
                onClick={handleNext}
                sx={{
                  borderRadius: 3,
                  px: 4,
                  py: 1,
                  background: 'linear-gradient(135deg, #4AA9A9 0%, #5FC0C1 100%)',
                  boxShadow: '0 4px 15px rgba(217, 119, 6, 0.4)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #1B3B4D 0%, #1B3B4D 100%)',
                    boxShadow: '0 6px 20px rgba(217, 119, 6, 0.6)'
                  }
                }}
              >
                Next →
              </Button>
            </Box>
          </Box>
        );

      case 2:
        return (
          <Box>
            <Box sx={{ mb: 3, textAlign: 'center' }}>
              <Typography variant="h4" fontWeight={700} sx={{ color: '#5FC0C1', mb: 1 }}>
                Supporting Documents
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Please upload the required documents.
              </Typography>
            </Box>

            <Button component="label" variant="contained" fullWidth sx={{ mt: 2, mb: 1 }}>
              Upload Student Photo
              <VisuallyHiddenInput name="photo" type="file" onChange={handleFileChange} accept="image/*" />
            </Button>
            {files.photo && <Typography variant="body2" sx={{ mt: 1, textAlign: 'center' }}>Selected: {files.photo.name}</Typography>}
            {fieldErrors.photo && <Alert severity="error" sx={{ mt: 1 }}>{typeof fieldErrors.photo === 'string' ? fieldErrors.photo : JSON.stringify(fieldErrors.photo)}</Alert>}
            
            <Button component="label" variant="contained" fullWidth sx={{ mt: 2, mb: 1 }}>
              Upload ID Card (Front)
              <VisuallyHiddenInput name="id_card_1" type="file" onChange={handleFileChange} accept="image/*,.pdf" />
            </Button>
            {files.id_card_1 && <Typography variant="body2" sx={{ mt: 1, textAlign: 'center' }}>Selected: {files.id_card_1.name}</Typography>}
            {fieldErrors.id_card_1 && <Alert severity="error" sx={{ mt: 1 }}>{typeof fieldErrors.id_card_1 === 'string' ? fieldErrors.id_card_1 : JSON.stringify(fieldErrors.id_card_1)}</Alert>}

            <Button component="label" variant="contained" fullWidth sx={{ mt: 2, mb: 1 }}>
              Upload ID Card (Back)
              <VisuallyHiddenInput name="id_card_2" type="file" onChange={handleFileChange} accept="image/*,.pdf" />
            </Button>
            {files.id_card_2 && <Typography variant="body2" sx={{ mt: 1, textAlign: 'center' }}>Selected: {files.id_card_2.name}</Typography>}
            {fieldErrors.id_card_2 && <Alert severity="error" sx={{ mt: 1 }}>{typeof fieldErrors.id_card_2 === 'string' ? fieldErrors.id_card_2 : JSON.stringify(fieldErrors.id_card_2)}</Alert>}

            <Button component="label" variant="contained" fullWidth sx={{ mt: 2, mb: 1 }}>
              Upload Proof of Registration/Acceptance
              <VisuallyHiddenInput name="proof_of_registration" type="file" onChange={handleFileChange} accept=".pdf" />
            </Button>
            {files.proof_of_registration && <Typography variant="body2" sx={{ mt: 1, textAlign: 'center' }}>Selected: {files.proof_of_registration.name}</Typography>}
            {fieldErrors.proof_of_registration && <Alert severity="error" sx={{ mt: 1 }}>{typeof fieldErrors.proof_of_registration === 'string' ? fieldErrors.proof_of_registration : JSON.stringify(fieldErrors.proof_of_registration)}</Alert>}

            {error && <Alert severity="error" sx={{ mt: 2 }}>{typeof error === 'string' ? error : JSON.stringify(error)}</Alert>}

            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
              <Button
                variant="outlined"
                onClick={handleBack}
                sx={{
                  borderRadius: 3,
                  px: 3,
                  py: 1,
                  borderColor: '#5FC0C1',
                  color: '#5FC0C1',
                  '&:hover': {
                    borderColor: '#1B3B4D',
                    backgroundColor: 'rgba(234, 88, 12, 0.04)'
                  }
                }}
              >
                ← Back
              </Button>
              <Button
                variant="contained"
                onClick={handleNext}
                sx={{
                  borderRadius: 3,
                  px: 4,
                  py: 1,
                  background: 'linear-gradient(135deg, #4AA9A9 0%, #5FC0C1 100%)',
                  boxShadow: '0 4px 15px rgba(217, 119, 6, 0.4)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #1B3B4D 0%, #1B3B4D 100%)',
                    boxShadow: '0 6px 20px rgba(217, 119, 6, 0.6)'
                  }
                }}
              >
                Next →
              </Button>
            </Box>
          </Box>
        );

      case 3:
        return (
          <Box>
            <Box sx={{ mb: 3, textAlign: 'center' }}>
              <Typography
                variant="h5"
                fontWeight={700}
                sx={{
                  color: '#5FC0C1',
                  mb: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 1
                }}
              >
                Guardian Information
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Parent or guardian contact details
              </Typography>
            </Box>

            <TextField
              label="Full Name"
              name="guardian_name"
              value={form.guardian_name}
              onChange={handleChange}
              required
              fullWidth
              margin="normal"
              error={!!fieldErrors.guardian_name}
              helperText={fieldErrors.guardian_name || "Enter guardian's full name"}
              placeholder="e.g. Jane Smith"
            />

            <TextField
              label="Relationship"
              name="guardian_relationship"
              value={form.guardian_relationship}
              onChange={handleChange}
              required
              fullWidth
              margin="normal"
              error={!!fieldErrors.guardian_relationship}
              helperText={fieldErrors.guardian_relationship}
              placeholder="e.g. Mother, Father, Guardian"
            />

            <TextField
              label="Phone Number"
              name="guardian_phone"
              value={form.guardian_phone}
              onChange={handleChange}
              required
              fullWidth
              margin="normal"
              error={!!fieldErrors.guardian_phone}
              helperText={fieldErrors.guardian_phone || 'Enter 10-digit phone number starting with 0'}
              placeholder="e.g. 0123456789"
              inputProps={{ maxLength: 10 }}
            />

            <TextField
              label="Email Address"
              type="email"
              name="guardian_email"
              value={form.guardian_email}
              onChange={handleChange}
              required
              fullWidth
              margin="normal"
              error={!!fieldErrors.guardian_email}
              helperText={fieldErrors.guardian_email || "Enter guardian's email address"}
              placeholder="e.g. mary@example.com"
            />

            {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}

            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
              <Button
                variant="outlined"
                onClick={handleBack}
                sx={{
                  borderRadius: 3,
                  px: 3,
                  py: 1,
                  borderColor: '#5FC0C1',
                  color: '#5FC0C1',
                  '&:hover': {
                    borderColor: '#1B3B4D',
                    backgroundColor: 'rgba(234, 88, 12, 0.04)'
                  }
                }}
              >
                ← Back
              </Button>
              <Button
                variant="contained"
                onClick={handleNext}
                sx={{
                  borderRadius: 3,
                  px: 4,
                  py: 1,
                  background: 'linear-gradient(135deg, #4AA9A9 0%, #5FC0C1 100%)',
                  boxShadow: '0 4px 15px rgba(217, 119, 6, 0.4)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #1B3B4D 0%, #1B3B4D 100%)',
                    boxShadow: '0 6px 20px rgba(217, 119, 6, 0.6)'
                  }
                }}
              >
                Next →
              </Button>
            </Box>
          </Box>
        );

      case 4:
        return (
          <Box sx={{ py: 2 }}>
            <Box sx={{ mb: 3, textAlign: 'center' }}>
              <Typography
                variant="h5"
                fontWeight={700}
                sx={{
                  color: '#5FC0C1',
                  mb: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 1
                }}
              >
                Review & Submit
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Please review your information before submitting to Riverview
              </Typography>
            </Box>

            <Paper elevation={1} sx={{ p: 3, mb: 3, bgcolor: '#fafafa' }}>
              <Typography variant="h6" gutterBottom sx={{ color: '#5FC0C1', fontWeight: 600 }}>
                Personal Information
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
                <Typography><strong>Year:</strong> {form.year}</Typography>
                <Typography><strong>Full Names:</strong> {form.full_names}</Typography>
                <Typography><strong>ID Number:</strong> {form.id_number}</Typography>
                <Typography><strong>Student Number:</strong> {form.student_number}</Typography>
                <Typography><strong>Institution:</strong> {form.institution}</Typography>
                <Typography><strong>Email:</strong> {form.email}</Typography>
                <Typography><strong>Phone:</strong> {form.phone}</Typography>
                <Typography><strong>Gender:</strong> {form.gender}</Typography>
                <Typography><strong>Ethnicity:</strong> {form.ethnicity}</Typography>
                <Typography><strong>Home Language:</strong> {form.home_language}</Typography>
              </Box>
              <Typography sx={{ mt: 2 }}><strong>Home Address:</strong> {form.home_address}</Typography>
            </Paper>

            <Paper elevation={1} sx={{ p: 3, mb: 3, bgcolor: '#fafafa' }}>
              <Typography variant="h6" gutterBottom sx={{ color: '#5FC0C1', fontWeight: 600 }}>
                Supporting Documents
              </Typography>
              <Typography><strong>Student Photo:</strong> {files.photo ? files.photo.name : 'Not Uploaded'}</Typography>
              <Typography><strong>ID Card (Front):</strong> {files.id_card_1 ? files.id_card_1.name : 'Not Uploaded'}</Typography>
              <Typography><strong>ID Card (Back):</strong> {files.id_card_2 ? files.id_card_2.name : 'Not Uploaded'}</Typography>
              <Typography><strong>Proof of Registration:</strong> {files.proof_of_registration ? files.proof_of_registration.name : 'Not Uploaded'}</Typography>
            </Paper>

            <Paper elevation={1} sx={{ p: 3, bgcolor: '#fafafa' }}>
              <Typography variant="h6" gutterBottom sx={{ color: '#5FC0C1', fontWeight: 600 }}>
                Guardian Information
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
                <Typography><strong>Guardian Name:</strong> {form.guardian_name}</Typography>
                <Typography><strong>Relationship:</strong> {form.guardian_relationship}</Typography>
                <Typography><strong>Guardian Phone:</strong> {form.guardian_phone}</Typography>
                <Typography><strong>Guardian Email:</strong> {form.guardian_email}</Typography>
              </Box>
            </Paper>

            {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}

            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
              <Button
                variant="outlined"
                onClick={handleBack}
                sx={{
                  borderRadius: 3,
                  px: 3,
                  py: 1,
                  borderColor: '#5FC0C1',
                  color: '#5FC0C1',
                  '&:hover': {
                    borderColor: '#1B3B4D',
                    backgroundColor: 'rgba(234, 88, 12, 0.04)'
                  }
                }}
              >
                ← Back
              </Button>
              <Button
                type="submit"
                variant="contained"
                disabled={loading}
                onClick={handleSubmit}
                sx={{
                  borderRadius: 3,
                  px: 4,
                  py: 1.5,
                  fontSize: 16,
                  fontWeight: 600,
                  background: 'linear-gradient(135deg, #4AA9A9 0%, #5FC0C1 100%)',
                  boxShadow: '0 4px 15px rgba(217, 119, 6, 0.4)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #1B3B4D 0%, #1B3B4D 100%)',
                    boxShadow: '0 6px 20px rgba(217, 119, 6, 0.6)'
                  },
                  '&:disabled': {
                    background: 'rgba(217, 119, 6, 0.3)',
                    color: 'white'
                  }
                }}
              >
                {loading ? (
                  <>
                    <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />
                    Submitting...
                  </>
                ) : (
                  'Submit Application'
                )}
              </Button>
            </Box>
          </Box>
        );

      default:
        return null;
    }
  };

  if (submitted) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: '#faf9f7', py: 4 }}>
        <Container maxWidth="md">
          <Paper elevation={3} sx={{ p: 4, borderRadius: 3, textAlign: 'center' }}>
            <Box sx={{ 
              fontSize: 80, 
              color: '#22c55e', 
              mb: 3,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center'
            }}>
              <Typography variant="h2" sx={{ color: '#22c55e' }}>●</Typography>
            </Box>
            <Typography variant="h4" gutterBottom sx={{ color: '#22c55e' }}>
              Application Submitted Successfully
            </Typography>
            <Typography variant="h6" color="text.secondary" gutterBottom>
              Thank you for applying to Riverview Premium Student Accommodation
            </Typography>
            <Typography sx={{ mt: 2 }}>
              <strong>Application ID:</strong> {applicationId}
            </Typography>
            <Button
              variant="contained"
              onClick={handleNewApplication}
              sx={{ mt: 3 }}
            >
              Submit Another Application
            </Button>
          </Paper>
        </Container>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#E0F5F5', py: 4 }}>
      {/* Header */}
      <Box sx={{ background: 'linear-gradient(135deg, #5FC0C1 0%, #1B3B4D 100%)', color: '#fff', py: 1, px: 2, mb: 4, borderRadius: '0 0 16px 16px' }}>
        <Container maxWidth="lg" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Box sx={{
              background: 'rgba(255,255,255,0.98)',
              borderRadius: '12px',
              padding: '8px 20px',
              display: 'flex',
              alignItems: 'center',
              boxShadow: '0 4px 16px rgba(27,59,77,0.15)',
              backdropFilter: 'blur(10px)',
              cursor: 'pointer'
            }} onClick={() => navigate('/')}>
              <img 
                src={process.env.PUBLIC_URL + '/Riverview Luxury Apartments-02.png'} 
                alt="Riverview Luxury Apartments" 
                style={{ 
                  height: '80px',
                  width: 'auto',
                  objectFit: 'contain',
                  display: 'block'
                }} 
              />
            </Box>
          </Box>
        </Container>
      </Box>
      
      <Container maxWidth="md">
        <Paper elevation={3} sx={{ p: 4, borderRadius: 3 }}>
          <Box sx={{ mb: 4 }}>
            <Stepper activeStep={step - 1} alternativeLabel>
              {steps.map((label) => (
                <Step key={label}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>
          </Box>

          <form onSubmit={handleSubmit}>
            {renderStepContent()}
          </form>
        </Paper>
      </Container>
    </Box>
  );
};

export default ApplicationForm;

